import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Save,
  Payment,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  LinkOff,
} from '@mui/icons-material';
import api from '../../services/api';

const StripeBillingContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    perBranchMonthlyPrice: 29.99,
    perCompanyMonthlyPrice: 49.99,
    enableResourceAddons: false,
    billingCurrency: 'usd',
    gracePeriodDays: 7,
    addonPricing: {
      storage: {
        free: { packageGB: 10, packagePrice: 9.99 },
        professional: { packageGB: 25, packagePrice: 14.99 },
        enterprise: { packageGB: 50, packagePrice: 24.99 },
      },
      bandwidth: {
        free: { packageGB: 100, packagePrice: 7.99 },
        professional: { packageGB: 250, packagePrice: 12.99 },
        enterprise: { packageGB: 500, packagePrice: 19.99 },
      },
    },
  });
  const [hasStripeSecretKey, setHasStripeSecretKey] = useState(false);
  const [hasStripeWebhookSecret, setHasStripeWebhookSecret] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stripe-billing');
      if (response.data.success && response.data.config) {
        const c = response.data.config;
        setConfig({
          enabled: c.enabled || false,
          stripeSecretKey: '', // Never show actual key
          stripeWebhookSecret: '', // Never show actual secret
          perBranchMonthlyPrice: c.perBranchMonthlyPrice ?? 29.99,
          perCompanyMonthlyPrice: c.perCompanyMonthlyPrice ?? 49.99,
          enableResourceAddons: (c.enableResourceAddons ?? (c.enableStorageOverageBilling || c.enableBandwidthOverageBilling)) || false,
          billingCurrency: c.billingCurrency || 'usd',
          gracePeriodDays: c.gracePeriodDays ?? 7,
          addonPricing: c.addonPricing || {
            storage: {
              free: { packageGB: 10, packagePrice: 9.99 },
              professional: { packageGB: 25, packagePrice: 14.99 },
              enterprise: { packageGB: 50, packagePrice: 24.99 },
            },
            bandwidth: {
              free: { packageGB: 100, packagePrice: 7.99 },
              professional: { packageGB: 250, packagePrice: 12.99 },
              enterprise: { packageGB: 500, packagePrice: 19.99 },
            },
          },
        });
        setHasStripeSecretKey(c.hasStripeSecretKey || false);
        setHasStripeWebhookSecret(c.hasStripeWebhookSecret || false);
      }
    } catch (err) {
      console.error('Error fetching Stripe billing config:', err);
      setError('Failed to load Stripe billing configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Only send secret key / webhook secret if they were actually changed (not empty)
      const data = { ...config };
      if (!data.stripeSecretKey) delete data.stripeSecretKey;
      if (!data.stripeWebhookSecret) delete data.stripeWebhookSecret;

      const response = await api.put('/stripe-billing', data);
      if (response.data.success) {
        setSuccess('Stripe billing configuration saved successfully!');
        setHasStripeSecretKey(response.data.config?.hasStripeSecretKey || hasStripeSecretKey);
        setHasStripeWebhookSecret(response.data.config?.hasStripeWebhookSecret || hasStripeWebhookSecret);
        // Clear key fields after save
        setConfig(prev => ({ ...prev, stripeSecretKey: '', stripeWebhookSecret: '' }));
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error saving Stripe billing config:', err);
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const response = await api.post('/stripe-billing/test-connection');
      if (response.data.success) {
        setTestResult({ success: true, account: response.data.account });
      }
    } catch (err) {
      setTestResult({
        success: false,
        error: err.response?.data?.error || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const updateAddonTierValue = (resourceType, tier, field, value) => {
    const normalizedValue = Number.isFinite(value)
      ? Number(Number(value).toFixed(2))
      : 0;
    setConfig(prev => ({
      ...prev,
      addonPricing: {
        ...prev.addonPricing,
        [resourceType]: {
          ...prev.addonPricing?.[resourceType],
          [tier]: {
            ...prev.addonPricing?.[resourceType]?.[tier],
            [field]: normalizedValue
          }
        }
      }
    }));
  };

  const updateTierUnifiedPrice = (tier, value) => {
    const normalizedValue = Number.isFinite(value)
      ? Number(Number(value).toFixed(2))
      : 0;
    setConfig(prev => ({
      ...prev,
      addonPricing: {
        ...prev.addonPricing,
        storage: {
          ...prev.addonPricing?.storage,
          [tier]: {
            ...prev.addonPricing?.storage?.[tier],
            packagePrice: normalizedValue
          }
        },
        bandwidth: {
          ...prev.addonPricing?.bandwidth,
          [tier]: {
            ...prev.addonPricing?.bandwidth?.[tier],
            packagePrice: normalizedValue
          }
        }
      }
    }));
  };

  const formatAddonGB = (value) => {
    const num = Number(value || 0);
    const isInt = Math.floor(num) === num;
    return `${num.toLocaleString(undefined, {
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: 2
    })} GB`;
  };

  const formatAddonPrice = (value) => {
    const num = Number(value || 0);
    return `$${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure Stripe platform billing for enterprise add-ons (per-branch, per-company,
          storage/bandwidth overages). This billing is separate from Apple IAP base subscriptions.
          <strong> All billing operations are disabled until the master toggle is enabled.</strong>
        </Typography>
      </Alert>

      {/* Master Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Payment sx={{ mr: 1, color: config.enabled ? '#4caf50' : '#f44336' }} />
            <Typography variant="h6">Stripe Platform Billing</Typography>
            <Chip
              label={config.enabled ? 'ENABLED' : 'DISABLED'}
              color={config.enabled ? 'success' : 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          </Box>
          <Divider sx={{ mb: 3 }} />
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                color="success"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Enable Stripe Billing
                </Typography>
              </Box>
            }
          />
          {!config.enabled && (
            <Alert severity="warning" icon={<Warning />} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Billing is currently disabled.</strong> No Stripe charges will be created.
                Enable this when you're ready to start billing for enterprise add-ons.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stripe API Keys */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Stripe API Keys</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Stripe Secret Key"
                type={showSecretKey ? 'text' : 'password'}
                value={config.stripeSecretKey}
                onChange={(e) => setConfig({ ...config, stripeSecretKey: e.target.value })}
                placeholder={hasStripeSecretKey ? '••••••••••••••••(key is set — leave blank to keep)' : 'sk_live_...'}
                helperText="Your Stripe secret key (starts with sk_live_ or sk_test_)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {hasStripeSecretKey && !config.stripeSecretKey && (
                        <Chip label="Set" color="success" size="small" sx={{ mr: 1 }} />
                      )}
                      <IconButton onClick={() => setShowSecretKey(!showSecretKey)} edge="end">
                        {showSecretKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Stripe Webhook Secret"
                type={showWebhookSecret ? 'text' : 'password'}
                value={config.stripeWebhookSecret}
                onChange={(e) => setConfig({ ...config, stripeWebhookSecret: e.target.value })}
                placeholder={hasStripeWebhookSecret ? '••••••••••••••••(secret is set — leave blank to keep)' : 'whsec_...'}
                helperText="Webhook signing secret for verifying Stripe events"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {hasStripeWebhookSecret && !config.stripeWebhookSecret && (
                        <Chip label="Set" color="success" size="small" sx={{ mr: 1 }} />
                      )}
                      <IconButton onClick={() => setShowWebhookSecret(!showWebhookSecret)} edge="end">
                        {showWebhookSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testing || !hasStripeSecretKey}
                startIcon={testing ? <CircularProgress size={20} /> : <LinkOff />}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              {testResult && (
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
                  sx={{ mt: 2 }}
                >
                  {testResult.success ? (
                    <Typography variant="body2">
                      Connected to <strong>{testResult.account?.businessName}</strong>
                      {' '}({testResult.account?.country}) — Charges: {testResult.account?.chargesEnabled ? '✅' : '❌'}
                      , Payouts: {testResult.account?.payoutsEnabled ? '✅' : '❌'}
                    </Typography>
                  ) : (
                    <Typography variant="body2">{testResult.error}</Typography>
                  )}
                </Alert>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pricing Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Add-On Pricing</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Per-Branch Monthly Price"
                type="number"
                value={config.perBranchMonthlyPrice}
                onChange={(e) => setConfig({ ...config, perBranchMonthlyPrice: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                helperText="Charged monthly for each branch beyond the tier allowance"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Per-Company Monthly Price"
                type="number"
                value={config.perCompanyMonthlyPrice}
                onChange={(e) => setConfig({ ...config, perCompanyMonthlyPrice: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                helperText="Charged monthly for each company beyond the tier allowance"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Currency"
                value={config.billingCurrency}
                onChange={(e) => setConfig({ ...config, billingCurrency: e.target.value.toLowerCase() })}
                helperText="ISO 4217 currency code (e.g., usd, eur, gbp)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grace Period (Days)"
                type="number"
                value={config.gracePeriodDays}
                onChange={(e) => setConfig({ ...config, gracePeriodDays: parseInt(e.target.value) || 0 })}
                helperText="Days before suspending service on payment failure (0-90)"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enableResourceAddons}
                  onChange={(e) => setConfig({ ...config, enableResourceAddons: e.target.checked })}
                />
              }
              label="Enable Shared Storage/Bandwidth Add-on Purchasing"
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" gutterBottom>
            Storage/Bandwidth Upgrade Packages (per tier)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure each tier as one shared package: storage size, bandwidth size, and a single package price.
          </Typography>
          <Grid container spacing={2}>
            {['free', 'standard', 'professional', 'enterprise'].map((tier) => {
              const unifiedPrice = config.addonPricing?.storage?.[tier]?.packagePrice
                ?? config.addonPricing?.bandwidth?.[tier]?.packagePrice
                ?? 0;
              const storageGB = config.addonPricing?.storage?.[tier]?.packageGB ?? 0;
              const bandwidthGB = config.addonPricing?.bandwidth?.[tier]?.packageGB ?? 0;
              return (
                <React.Fragment key={`tier-${tier}`}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mt: 1 }}>
                      {tier} Tier Package
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Storage Package Size (GB)"
                      type="number"
                      value={storageGB}
                      onChange={(e) => updateAddonTierValue('storage', tier, 'packageGB', parseFloat(e.target.value) || 0)}
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Bandwidth Package Size (GB)"
                      type="number"
                      value={bandwidthGB}
                      onChange={(e) => updateAddonTierValue('bandwidth', tier, 'packageGB', parseFloat(e.target.value) || 0)}
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Package Price"
                      type="number"
                      value={unifiedPrice}
                      onChange={(e) => updateTierUnifiedPrice(tier, parseFloat(e.target.value) || 0)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      inputProps={{ step: '0.01', min: 0 }}
                      helperText={`Customers see: ${formatAddonGB(storageGB)} storage + ${formatAddonGB(bandwidthGB)} bandwidth for ${formatAddonPrice(unifiedPrice)}`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Stripe Billing Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default StripeBillingContent;

